import type { AnalyticsPlugin } from 'analytics';
import Cookies from 'js-cookie';

// Shared with CNUFE (KRKN-11231) and web-acquisitions, which read it to know a FullStory
// session was identified and must be anonymized after logout. Keep name, value and scope in sync.
export const FS_IDENTIFIED_COOKIE = 'fs_identified';

export const FS_POLL_INTERVAL_MS = 250;
export const FS_POLL_TIMEOUT_MS = 10_000;

const ACCOUNT_ID_PATTERN = /^\d+$/;

type FullStoryIdentityT = {
  properties: { displayName: string; };
  uid: string;
};

type FullStoryApiT = (method: 'setIdentity', options: FullStoryIdentityT) => void;

type FullStoryWindowT = { FS?: FullStoryApiT; } & Window;

declare let window: FullStoryWindowT;

type ConfigT = {
  // Domain/path/secure for the flag cookie. Pass the same attributes the shared JWT cookies use
  // (defaultCookieAttrs() from @consumer-frontend/api) so every CNU host can read it.
  cookieAttributes: Cookies.CookieAttributes;
  enabled: boolean;
};

type IdentifyCallbackT = {
  payload: { userId?: unknown; };
};

// Shown in the FullStory UI instead of the bare uid. It's built from the account ID only, so it
// sends nothing beyond the uid itself.
export const fullStoryDisplayName = (uid: string): string => `Customer #${uid}`;

const getFS = (): FullStoryApiT | undefined => (
  typeof window.FS === 'function' ? window.FS : undefined
);

export function buildFullStoryPlugin(config: ConfigT): AnalyticsPlugin {
  let identifiedUid: string | undefined;
  let pollTimer: ReturnType<typeof setInterval> | undefined;

  const stopPolling = () => {
    clearInterval(pollTimer);
    pollTimer = undefined;
  };

  const setIdentity = (fs: FullStoryApiT, uid: string) => {
    fs('setIdentity', { properties: { displayName: fullStoryDisplayName(uid) }, uid });
    identifiedUid = uid;
    // Session cookie: no `expires`, so it dies with the browser session.
    Cookies.set(FS_IDENTIFIED_COOKIE, '1', { ...config.cookieAttributes, path: '/' });
  };

  return {
    config: { enabled: config.enabled },
    identify: ({ payload }: IdentifyCallbackT) => {
      const uid = payload.userId == null ? '' : String(payload.userId);

      if (!ACCOUNT_ID_PATTERN.test(uid) || uid === identifiedUid) return;

      stopPolling();

      const fs = getFS();

      if (fs) {
        setIdentity(fs, uid);

        return;
      }

      // FullStory is injected asynchronously by Adobe Launch, so it may not exist yet. Browser
      // API v2 has no documented ready callback (`_fs_ready` is v1 only), so poll for a while.
      const startedAt = Date.now();

      pollTimer = setInterval(() => {
        const loadedFS = getFS();

        if (loadedFS) {
          stopPolling();
          setIdentity(loadedFS, uid);
        } else if (Date.now() - startedAt >= FS_POLL_TIMEOUT_MS) {
          stopPolling();
        }
      }, FS_POLL_INTERVAL_MS);
    },
    name: 'fullstory',
    // FullStory itself is anonymized by CNUFE after logout; this only forgets local state.
    reset: () => {
      stopPolling();
      identifiedUid = undefined;
    },
  };
}
